/**
 * Selectores PUROS sobre la vista de batería del API (BateriaCompletaDTO): qué
 * ejercicios toca administrar hoy y con qué ítems. Sin React ni red, para poder
 * testear la planificación de la sesión aparte del reproductor.
 *
 * Sesión de administración (master §2.2): la banda A parte la fonética en dos
 * (F1+F2 / F3+F4) mediante `sesionNumero`. La fonológica no tiene partición
 * propia, así que se administra junto con la ÚLTIMA sesión de la fonética: la
 * banda A queda en las 2 sesiones que manda la spec y las bandas B–D en una
 * sola (con pausa entre ejercicios). El orden entre evaluaciones lo impone R60.
 */
import type { BateriaCompletaDTO, BateriaResumen, EjercicioDTO, PeffApoyo } from "../api/types.js";
import type { EjercicioRun, ItemRun } from "./peff-player.js";

const PENDIENTE = new Set(["pendiente", "en_progreso"]);

/** Estados "en curso" que se RETOMAN sobre la MISMA batería (nunca se crea una
 *  nueva). Es la clave del cierre de banda A: la 2.ª sesión vive en la batería
 *  ya creada, así que empezar otra la dejaría para siempre en 'en_progreso'. */
const REANUDABLE = new Set<BateriaResumen["estado"]>([
  "creada",
  "mic_check",
  "en_progreso",
  "suspendida",
]);

export function esReanudable(estado: BateriaResumen["estado"]): boolean {
  return REANUDABLE.has(estado);
}

/** Batería en curso a retomar, si la hay (la más reciente: el historial viene
 *  ordenado DESC). Solo retoma una del MISMO modo que el pedido: una batería de
 *  PRUEBA (QA) y una clínica normal JAMÁS se cruzan al reanudar. Sin este filtro,
 *  pedir QA mientras hay una normal en curso (típico en banda A, cuya 2.ª sesión
 *  queda 'en_progreso') retomaba la normal —plan completo, F4=69— e ignoraba el
 *  toggle. Si no hay ninguna del modo pedido, el llamador crea una nueva. */
export function bateriaReanudable(
  historial: readonly BateriaResumen[],
  modoPrueba = false,
): BateriaResumen | null {
  return historial.find((b) => esReanudable(b.estado) && b.modoPrueba === modoPrueba) ?? null;
}

/** Número de sesión efectivo del ejercicio dentro de la batería. La fonológica
 *  se administra con la ÚLTIMA sesión de la fonética (R60). El plan ya persiste
 *  su `sesionNumero` correcto, pero derivarlo del máximo de la fonética mantiene
 *  la coherencia también para baterías creadas ANTES del fix (PSF con
 *  sesion_numero=1 en banda A): así nunca se agrupa la fonológica en una sesión
 *  anterior a F3/F4. */
function sesionDe(b: BateriaCompletaDTO, ej: EjercicioDTO): number {
  const evaluacion = b.evaluaciones.find((e) => e.id === ej.evaluacionId);
  if (evaluacion?.tipo === "FONETICA") return ej.sesionNumero;
  const sesionesFonetica = b.ejercicios
    .filter((x) => b.evaluaciones.find((e) => e.id === x.evaluacionId)?.tipo === "FONETICA")
    .map((x) => x.sesionNumero);
  return sesionesFonetica.length > 0 ? Math.max(...sesionesFonetica) : ej.sesionNumero;
}

/** Orden de administración: evaluación (R60) → orden del ejercicio. */
function ordenDe(b: BateriaCompletaDTO, ej: EjercicioDTO): [number, number] {
  const evaluacion = b.evaluaciones.find((e) => e.id === ej.evaluacionId);
  return [evaluacion?.orden ?? 99, ej.orden];
}

export interface PlanSesion {
  ejercicios: EjercicioRun[];
  /** Quedan ejercicios para otro día (banda A tras F1+F2). */
  haySesionPendiente: boolean;
  /** Sesión de administración en curso (1 o 2). */
  sesionNumero: number;
}

/**
 * Ejercicios pendientes de la sesión en curso, con sus ítems y apoyos.
 * `apoyos` viene de word-bank (`/v1/word-bank/peff/apoyos`); lo que falte queda
 * en `null` y el reproductor cae a un placeholder TTS marcado.
 */
export function planSesion(
  b: BateriaCompletaDTO,
  apoyos: ReadonlyMap<string, PeffApoyo> = new Map(),
): PlanSesion {
  const pendientes = b.ejercicios
    .filter((e) => PENDIENTE.has(e.estado))
    .sort((x, y) => {
      const [ex, ox] = ordenDe(b, x);
      const [ey, oy] = ordenDe(b, y);
      return ex - ey || ox - oy;
    });

  if (pendientes.length === 0) {
    return { ejercicios: [], haySesionPendiente: false, sesionNumero: 1 };
  }

  const sesiones = pendientes.map((e) => sesionDe(b, e));
  const sesionNumero = Math.min(...sesiones);
  const deHoy = pendientes.filter((e) => sesionDe(b, e) === sesionNumero);

  return {
    ejercicios: deHoy.map((e) => ({
      id: e.id,
      codigo: e.codigo,
      items: itemsDe(b, e.id, apoyos),
    })),
    haySesionPendiente: sesiones.some((n) => n > sesionNumero),
    sesionNumero,
  };
}

/** Ítems de un ejercicio, en orden, enriquecidos con los apoyos de word-bank. */
export function itemsDe(
  b: BateriaCompletaDTO,
  ejercicioId: string,
  apoyos: ReadonlyMap<string, PeffApoyo> = new Map(),
): ItemRun[] {
  return b.items
    .filter((i) => i.ejercicioId === ejercicioId)
    .sort((x, y) => x.orden - y.orden)
    .map((i) => {
      const apoyo = i.wordId ? apoyos.get(i.wordId) : undefined;
      return {
        id: i.id,
        ref: i.ref,
        wordId: i.wordId,
        phoneme: i.phoneme,
        pistaSemantica: apoyo?.pistaSemantica ?? null,
        preguntaDistractora: apoyo?.preguntaDistractora ?? null,
        imagenUrl: apoyo?.imagenUrl ?? null,
        audioModeloUrl: apoyo?.audioModeloUrl ?? null,
      };
    });
}

/** wordIds de los ítems de la sesión, para pedir sus apoyos en un solo viaje. */
export function wordIdsDe(b: BateriaCompletaDTO): string[] {
  return [...new Set(b.items.map((i) => i.wordId).filter((w): w is string => !!w))];
}

/** La batería ya no admite administración (procesada, cancelada, etc.). */
export function estaCerrada(b: BateriaCompletaDTO): boolean {
  return ["procesando", "procesada", "inconcluyente", "cancelada"].includes(b.estado);
}

/** Estados en los que POST /ejercicios/:id/start es válido (la batería aún se
 *  administra). En cualquier otro el backend responde 409. */
export function bateriaAdministrable(b: BateriaCompletaDTO): boolean {
  return b.estado === "mic_check" || b.estado === "en_progreso";
}

/** La administración YA terminó y la batería entró a la fase de cierre/scoring
 *  (típicamente por sí sola: al descartarse el último adaptativo pasó a
 *  'completada' → 'procesando'). Ante un 409 de `start` con la batería aquí, la
 *  UI transiciona a la pantalla de cierre en vez de reintentar. No incluye
 *  'suspendida'/'cancelada' (ahí el start no debe fingir un cierre). */
export function bateriaEnCierre(b: BateriaCompletaDTO): boolean {
  return ["completada", "procesando", "procesada", "inconcluyente"].includes(b.estado);
}

/** El ejercicio ya es terminal en el backend (completado o descartado). Un
 *  adaptativo (F3/PSF2) sin ítems elegibles se descarta al cerrar su
 *  denominación hermana, así que el `start` del cliente sobre él responde 409;
 *  hay que saltarlo (cierre por ítems vacíos), no reintentarlo. */
export function ejercicioYaCerrado(b: BateriaCompletaDTO, ejercicioId: string): boolean {
  const ej = b.ejercicios.find((e) => e.id === ejercicioId);
  return !!ej && (ej.estado === "completado" || ej.estado === "descartado");
}
